import React, { useState, useEffect } from 'react';
import { researchStatusLabel } from '../utils/researchStatus';
import { currentLocale, targetingLabel } from '../i18n';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ResearchAIAnalysisPanel from './ResearchAIAnalysisPanel';
import { API_BASE_URL } from '../config/api';
import '../styles/research-dashboard.css';
import '../styles/research-share.css';
import { getBranding } from '../config/branding';
import ResearchShareDievo, { ResearchData as DievoResearchData } from './ResearchShareDievo';

interface ResearchData {
  id: number;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  respondent_count: number;
  target_respondents?: number;
  current_respondents: number;
  progress_percent?: number;
  questions: QuestionResult[];
  creative_breaks?: {
    id: number;
    position: number;
    question_text: string | null;
    media_url: string | null;
    media_type: string | null;
    media_description?: string | null;
  }[];
  demographics: {
    age: { range: string; count: number; percentage: number }[];
    gender: { label: string; count: number; percentage: number }[];
    geo: { region: string; count: number; percentage: number }[];
  };
  progress?: {
    launched_at: string;
    original_launched_at: string;
    duration_days_min: number;
    duration_days_max: number;
    is_complete: boolean;
  };
}

interface QuestionResult {
  id: number;
  position: number;
  question_text: string;
  question_type: string;
  answers: AnswerResult[];
  demographic_breakdown: DemographicBreakdown[];
}

interface AnswerResult {
  id: number;
  answer_text: string;
  answer_image?: string | null;   // creative_test: option image URL
  count: number;
  percentage: number;
}

const imgUrl = (u?: string | null) => u ? `${API_BASE_URL.replace(/\/$/, '')}${u}` : '';

interface DemographicBreakdown {
  group: string;
  answers: { answer_text: string; percentage: number }[];
}

// Answer color palette (same as main dashboard)
const ANSWER_COLORS = [
  '#FF6B4A', '#6B3FA0', '#FFAB4A', '#E94B6C', '#4A4A6A',
  '#FF8C69', '#8E3A9A', '#D4956A', '#C74B8C', '#7A5C8A',
];
const AGE_COLORS = ['#FF6B4A', '#FBBF24', '#22C55E', '#3B82F6', '#A855F7', '#EC4899'];
const GENDER_COLORS = ['#E94B6C', '#9B4DA8'];
const GEO_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Donut chart (same as main dashboard)
interface DonutChartProps {
  data: { label: string; value: number; percentage: number; color: string }[];
  size?: number;
  title: string;
  maxLegendItems?: number;
}

const DonutChart: React.FC<DonutChartProps> = ({ data, size = 200, title, maxLegendItems }) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    const rect = e.currentTarget.closest('.donut-chart-wrapper')?.getBoundingClientRect();
    if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10 });
    setHoveredIndex(index);
  };
  const legendData = maxLegendItems && data.length > maxLegendItems
    ? [
        ...data.slice(0, maxLegendItems),
        {
          label: t('research.others'),
          value: data.slice(maxLegendItems).reduce((sum, item) => sum + item.value, 0),
          percentage: data.slice(maxLegendItems).reduce((sum, item) => sum + item.percentage, 0),
          color: '#9CA3AF',
        },
      ]
    : data;

  const center = size / 2;
  const outerRadius = size / 2 - 10;
  const innerRadius = outerRadius * 0.5;
  const labelRadius = (outerRadius + innerRadius) / 2;

  const polarToCartesian = (angle: number, radius: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(radians),
      y: center + radius * Math.sin(radians),
    };
  };

  const createArcPath = (startAngle: number, endAngle: number) => {
    if (endAngle - startAngle >= 359.99) {
      return `M ${center} ${center - outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center + outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center - outerRadius}
              M ${center} ${center - innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center + innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center - innerRadius} Z`;
    }

    const startOuter = polarToCartesian(startAngle, outerRadius);
    const endOuter = polarToCartesian(endAngle, outerRadius);
    const startInner = polarToCartesian(endAngle, innerRadius);
    const endInner = polarToCartesian(startAngle, innerRadius);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${startOuter.x} ${startOuter.y}
            A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}
            L ${startInner.x} ${startInner.y}
            A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}
            Z`;
  };

  const segments: { label: string; value: number; startAngle: number; endAngle: number; percentage: number; color: string }[] = [];
  let angleCalc = -90;
  data.forEach((segment) => {
    const angle = (segment.percentage / 100) * 360;
    segments.push({
      label: segment.label,
      value: segment.value,
      startAngle: angleCalc,
      endAngle: angleCalc + angle,
      percentage: segment.percentage,
      color: segment.color,
    });
    angleCalc += angle;
  });

  return (
    <div className="donut-chart-container">
      <h4 className="donut-chart-title">{title}</h4>
      <div className="donut-chart-wrapper">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments.map((segment, index) => {
            if (segment.percentage < 0.5) return null;
            return (
              <path
                key={index}
                d={createArcPath(segment.startAngle, segment.endAngle - 0.5)}
                fill={segment.color}
                className={`donut-segment ${hoveredIndex === index ? 'hovered' : ''}`}
                style={{ animationDelay: `${index * 0.08}s` }}
                onMouseMove={(e) => handleMouseMove(e, index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
          {segments.map((segment, index) => {
            if (segment.percentage < 5) return null;
            const midAngle = (segment.startAngle + segment.endAngle) / 2;
            const labelPos = polarToCartesian(midAngle, labelRadius);
            return (
              <text
                key={`label-${index}`}
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="donut-label"
              >
                {segment.percentage.toFixed(0)}%
              </text>
            );
          })}
        </svg>
        {hoveredIndex !== null && segments[hoveredIndex] && (
          <div className="donut-tooltip" style={{ left: tooltipPos.x, top: tooltipPos.y }}>
            <div className="donut-tooltip-label">{segments[hoveredIndex].label}</div>
            <div className="donut-tooltip-value">{segments[hoveredIndex].value.toLocaleString(currentLocale())}</div>
            <div className="donut-tooltip-percent">{segments[hoveredIndex].percentage.toFixed(1)}%</div>
          </div>
        )}
      </div>
      <div className="donut-legend">
        {legendData.map((item, index) => (
          <div key={index} className="donut-legend-item">
            <span className="donut-legend-color" style={{ background: item.color }} />
            <span className="donut-legend-label">{item.label}</span>
            <span className="donut-legend-value">{item.value.toLocaleString(currentLocale())}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Pie chart (same as main dashboard)
interface PieChartProps {
  data: { label: string; count: number; percentage: number; color: string }[];
  size?: number;
}

const PieChart: React.FC<PieChartProps> = ({ data, size = 180 }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    const rect = e.currentTarget.closest('.question-pie-wrapper')?.getBoundingClientRect();
    if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10 });
    setHoveredIndex(index);
  };
  const center = size / 2;
  const outerRadius = size / 2 - 4;
  const innerRadius = outerRadius * 0.5;
  const labelRadius = (outerRadius + innerRadius) / 2;

  const polarToCartesian = (angle: number, r: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + r * Math.cos(radians),
      y: center + r * Math.sin(radians),
    };
  };

  const createArcPath = (startAngle: number, endAngle: number) => {
    if (endAngle - startAngle >= 359.99) {
      return `M ${center} ${center - outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center + outerRadius}
              A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center - outerRadius}
              M ${center} ${center - innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center + innerRadius}
              A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center - innerRadius} Z`;
    }

    const startOuter = polarToCartesian(startAngle, outerRadius);
    const endOuter = polarToCartesian(endAngle, outerRadius);
    const startInner = polarToCartesian(endAngle, innerRadius);
    const endInner = polarToCartesian(startAngle, innerRadius);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${startOuter.x} ${startOuter.y}
            A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}
            L ${startInner.x} ${startInner.y}
            A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}
            Z`;
  };

  const segments: { label: string; count: number; startAngle: number; endAngle: number; percentage: number; color: string }[] = [];
  let angleCalc = -90;
  data.forEach((segment) => {
    const angle = (segment.percentage / 100) * 360;
    segments.push({
      label: segment.label,
      count: segment.count,
      startAngle: angleCalc,
      endAngle: angleCalc + angle,
      percentage: segment.percentage,
      color: segment.color,
    });
    angleCalc += angle;
  });

  return (
    <div className="question-pie-wrapper">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="pie-chart">
        {segments.map((segment, index) => {
          if (segment.percentage < 0.5) return null;
          return (
            <path
              key={index}
              d={createArcPath(segment.startAngle, segment.endAngle - 0.5)}
              fill={segment.color}
              className={`pie-segment ${hoveredIndex === index ? 'hovered' : ''}`}
              style={{ animationDelay: `${index * 0.08}s` }}
              onMouseMove={(e) => handleMouseMove(e, index)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          );
        })}
        {segments.map((segment, index) => {
          if (segment.percentage < 8) return null;
          const midAngle = (segment.startAngle + segment.endAngle) / 2;
          const labelPos = polarToCartesian(midAngle, labelRadius);
          return (
            <text
              key={`label-${index}`}
              x={labelPos.x}
              y={labelPos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="pie-label"
            >
              {segment.percentage.toFixed(0)}%
            </text>
          );
        })}
      </svg>
      {hoveredIndex !== null && segments[hoveredIndex] && (
        <div className="donut-tooltip" style={{ left: tooltipPos.x, top: tooltipPos.y }}>
          <div className="donut-tooltip-label">{segments[hoveredIndex].label}</div>
          <div className="donut-tooltip-value">{segments[hoveredIndex].count.toLocaleString(currentLocale())}</div>
          <div className="donut-tooltip-percent">{segments[hoveredIndex].percentage.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};

// Horizontal bar chart for multiple_choice (same as main dashboard)
interface HorizontalBarChartProps {
  data: { label: string; count: number; percentage: number; color: string }[];
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ data }) => {
  const maxPercentage = Math.max(...data.map(d => d.percentage), 100);

  return (
    <div className="horizontal-bar-chart">
      {data.map((item, index) => (
        <div key={index} className="horizontal-bar-row">
          <div className="horizontal-bar-label" title={item.label}>
            {item.label}
          </div>
          <div className="horizontal-bar-track">
            <div
              className="horizontal-bar-fill"
              style={{
                width: `${(item.percentage / maxPercentage) * 100}%`,
                backgroundColor: item.color,
              }}
            >
              <span className="horizontal-bar-value-inside">{item.percentage.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Stacked bar chart (same as main dashboard)
interface StackedBarChartProps {
  breakdown: DemographicBreakdown[];
  answers: AnswerResult[];
}

const SEGMENT_LABEL_MIN_PCT = 10;

const StackedBarChart: React.FC<StackedBarChartProps> = ({ breakdown, answers }) => {
  const [hoveredSegment, setHoveredSegment] = useState<{ group: string; answer: string; percentage: number } | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const answerColors = answers.reduce((acc, answer, index) => {
    acc[answer.answer_text] = ANSWER_COLORS[index % ANSWER_COLORS.length];
    return acc;
  }, {} as Record<string, string>);

  const handleMouseMove = (e: React.MouseEvent, group: string, answerText: string, percentage: number) => {
    const rect = e.currentTarget.closest('.stacked-bar-chart')?.getBoundingClientRect();
    if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10 });
    setHoveredSegment({ group, answer: answerText, percentage });
  };

  return (
    <div className="stacked-bar-chart">
      <div className="stacked-bar-axis">
        <span>0%</span>
        <span>25%</span>
        <span>50%</span>
        <span>75%</span>
        <span>100%</span>
      </div>
      <div className="stacked-bars">
        {breakdown.map((group, groupIndex) => {
          // Scale each column by its own total so it always fills and the eye compares composition.
          const groupTotal = group.answers.reduce((sum, a) => sum + (a.percentage || 0), 0) || 100;
          return (
          <div key={groupIndex} className="stacked-bar-column">
            <div className="stacked-bar">
              {group.answers.map((answer, answerIndex) => {
                const shareOfColumn = (answer.percentage / groupTotal) * 100;
                return (
                <div
                  key={answerIndex}
                  className={`stacked-bar-segment ${hoveredSegment?.group === group.group && hoveredSegment?.answer === answer.answer_text ? 'hovered' : ''}`}
                  style={{
                    height: `${shareOfColumn}%`,
                    background: answerColors[answer.answer_text],
                    animationDelay: `${groupIndex * 0.05 + answerIndex * 0.02}s`,
                  }}
                  onMouseMove={(e) => handleMouseMove(e, group.group, answer.answer_text, answer.percentage)}
                  onMouseLeave={() => setHoveredSegment(null)}
                >
                  {shareOfColumn >= SEGMENT_LABEL_MIN_PCT && (
                    <span className="stacked-bar-value">{answer.percentage.toFixed(0)}%</span>
                  )}
                </div>
                );
              })}
            </div>
            <span className="stacked-bar-label">{group.group}</span>
          </div>
          );
        })}
      </div>
      {hoveredSegment && (
        <div className="stacked-bar-tooltip" style={{ left: tooltipPos.x, top: tooltipPos.y }}>
          <div className="stacked-bar-tooltip-group">{hoveredSegment.group}</div>
          <div className="stacked-bar-tooltip-answer">{hoveredSegment.answer}</div>
          <div className="stacked-bar-tooltip-percent">{hoveredSegment.percentage.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};

const ResearchSharePage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ResearchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/sense/research/share/${token}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(t('research.notFound'));
          } else if (response.status === 403) {
            throw new Error(t('research.notShared'));
          }
          throw new Error(t('research.fetchFailed'));
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('research.unknownError'));
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="share-page">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>{t('research.loadingData')}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="share-page">
        <div className="dashboard-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <circle cx="12" cy="16" r="0.5" fill="currentColor" />
          </svg>
          <p>{error || t('research.notFound')}</p>
        </div>
      </div>
    );
  }

  // DIEVO edition gets the redesigned Share view; Sense keeps this one untouched.
  if (getBranding().edition === 'dievo') {
    return <ResearchShareDievo data={data as unknown as DievoResearchData} token={token || ''} />;
  }

  const progressPercentage = data.respondent_count > 0
    ? Math.min(100, (data.current_respondents / data.respondent_count) * 100)
    : 0;

  // Interleave creative_display "breaks" with question cards, ordered by scenario position,
  // and number questions sequentially (Q1..Qn, breaks not counted).
  const scenarioSteps: any[] = [];
  (data.creative_breaks || []).forEach((b) => scenarioSteps.push({ kind: 'creative', position: b.position, step: b }));
  data.questions.forEach((q) => scenarioSteps.push({ kind: 'question', position: q.position, question: q }));
  scenarioSteps.sort((a, b) => (a.position || 0) - (b.position || 0));
  let __qn = 0;
  scenarioSteps.forEach((s) => { if (s.kind === 'question') { __qn += 1; s.number = __qn; } });

  return (
    <div className="share-page research-dashboard">
      {/* Header */}
      <header className="share-header">
        <div className="share-logo">
          <img src="/logo.png" alt={getBranding().brand_name} />
        </div>
        <div className="share-meta">
          {t('research.generated')} {new Date().toLocaleDateString(currentLocale())}
        </div>
      </header>

      {/* Title */}
      <div className="dashboard-header share-title-section">
        <div className="dashboard-header-left">
          <h2 className="dashboard-title">{data.name}</h2>
          <span className={`dashboard-status status-${data.status.toLowerCase()}`}>
            {researchStatusLabel(data.status)}
          </span>
        </div>
        <div className="dashboard-header-right">
          <ResearchAIAnalysisPanel
            researchStatus={data.status}
            getUrl={`/api/sense/research/share/${token}/ai-analysis`}
          />
        </div>
      </div>

      {/* Progress Section */}
      <div className="dashboard-progress-section">
        <div className="progress-info">
          <div className="progress-numbers">
            <span className="progress-current">{data.current_respondents.toLocaleString(currentLocale())}</span>
            <span className="progress-separator">/</span>
            <span className="progress-target">{data.respondent_count.toLocaleString(currentLocale())}</span>
            <span className="progress-label">{t('research.respondentsLabel')}</span>
          </div>
          <span className="progress-percentage">{progressPercentage.toFixed(1)}%</span>
        </div>
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        {data.progress?.original_launched_at && (
          <div className="progress-dates">
            <div className="progress-date-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>
                Started:{' '}
                <strong>
                  {new Date(data.progress.original_launched_at).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' })}
                </strong>
              </span>
            </div>
          </div>
        )}
        {data.progress?.is_complete && (
          <div className="progress-complete">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>
              {t('research.researchCompleted')}
              {data.progress.original_launched_at && (
                <span className="progress-complete-dates">
                  {' · '}
                  {new Date(data.progress.original_launched_at).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })}
                  {' — '}
                  {(() => {
                    const start = new Date(data.progress.original_launched_at);
                    const avgDays = (data.progress.duration_days_min + data.progress.duration_days_max) / 2;
                    const end = new Date(start.getTime() + avgDays * 24 * 60 * 60 * 1000);
                    return end.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
                  })()}
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Demographics Section */}
      <div className="dashboard-section">
        <h3 className="section-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          {t('research.passport')}
        </h3>
        <div className="demographics-grid">
          <DonutChart
            title={t('research.ageDistribution')}
            data={data.demographics.age.map((item, index) => ({
              label: item.range,
              value: item.count,
              percentage: item.percentage,
              color: AGE_COLORS[index % AGE_COLORS.length],
            }))}
          />
          <DonutChart
            title={t('research.genderDistribution')}
            data={data.demographics.gender.map((item, index) => ({
              label: targetingLabel(item.label),
              value: item.count,
              percentage: item.percentage,
              color: GENDER_COLORS[index % GENDER_COLORS.length],
            }))}
          />
          <DonutChart
            title={t('research.geography')}
            maxLegendItems={5}
            data={(data.demographics.geo || []).map((item, index) => ({
              label: item.region,
              value: item.count,
              percentage: item.percentage,
              color: GEO_COLORS[index % GEO_COLORS.length],
            }))}
          />
        </div>
      </div>

      {/* Questions Results Section */}
      <div className="dashboard-section">
        <h3 className="section-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <circle cx="12" cy="17" r="0.5" fill="currentColor" />
          </svg>
          {t('research.questionResults')}
        </h3>
        <div className="questions-results">
          {scenarioSteps.map((item: any, __i: number) => item.kind === 'creative' ? (
            <div key={`c${__i}`} className="question-result-card creative-break-card" style={{ padding: 16, textAlign: 'center' }}>
              {item.step.media_url && (
                item.step.media_type === 'video' ? (
                  <video src={imgUrl(item.step.media_url)} controls style={{ display: 'block', maxWidth: '100%', maxHeight: 320, margin: '0 auto', borderRadius: 10, background: '#000' }} />
                ) : (
                  <img src={imgUrl(item.step.media_url)} alt="" style={{ display: 'block', maxWidth: '100%', maxHeight: 320, margin: '0 auto', borderRadius: 10 }} />
                )
              )}
              {item.step.question_text && (
                <div style={{ fontSize: 15, color: 'var(--text-secondary, #868e96)', marginTop: 12 }}>{item.step.question_text}</div>
              )}
            </div>
          ) : (() => {
            const question: QuestionResult = item.question;
            return (
            <div key={question.id} className="question-result-card expanded">
              <div className="question-result-header">
                <div className="question-number">Q{item.number}</div>
                <div className="question-text">{question.question_text}</div>
              </div>

              <div className="question-result-content">
                <div className="question-charts-row">
                  {/* Chart section */}
                  <div className={`question-pie-section ${question.question_type === 'multiple_choice' ? 'multiple-choice-section' : ''}`}>
                    {question.question_type === 'multiple_choice' ? (
                      <>
                        <div className="multiple-choice-label">{t('research.multipleAnswers')}</div>
                        <HorizontalBarChart
                          data={question.answers.map((answer, index) => ({
                            label: answer.answer_text,
                            count: answer.count,
                            percentage: answer.percentage,
                            color: ANSWER_COLORS[index % ANSWER_COLORS.length],
                          }))}
                        />
                      </>
                    ) : (
                      <>
                        <PieChart
                          data={question.answers.map((answer, index) => ({
                            label: answer.answer_text,
                            count: answer.count,
                            percentage: answer.percentage,
                            color: ANSWER_COLORS[index % ANSWER_COLORS.length],
                          }))}
                        />
                        <div className={`question-legend ${question.question_type === 'creative_test' ? 'creative-legend' : ''}`}>
                          {question.answers.map((answer, index) => (
                            <div key={answer.id} className="question-legend-item">
                              <span
                                className="legend-color"
                                style={{ background: ANSWER_COLORS[index % ANSWER_COLORS.length] }}
                              />
                              {question.question_type === 'creative_test' && answer.answer_image ? (
                                <img className="legend-thumb" src={imgUrl(answer.answer_image)} alt={`Option ${index + 1}`} />
                              ) : (
                                <span className="legend-text">{answer.answer_text}</span>
                              )}
                              <span className="legend-value">{answer.count.toLocaleString(currentLocale())} ({answer.percentage}%)</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Stacked bar chart */}
                  {question.demographic_breakdown.length > 0 && (
                    <div className="question-breakdown-section">
                      <h5 className="breakdown-title">{t('research.breakdownDemographics')}</h5>
                      <StackedBarChart
                        breakdown={question.demographic_breakdown}
                        answers={question.answers}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
              );
            })())}
        </div>
      </div>

      {/* Print button */}
      <div className="share-actions">
        <button onClick={() => window.print()} className="dashboard-btn dashboard-btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          {t('research.print')}
        </button>
      </div>
    </div>
  );
};

export default ResearchSharePage;
